/* Liquid metal backdrop: one full-screen WebGL quad, no library.
   Same palette as the plugin's own backdrop (#0b0b0e ground, #c8cbd0 chrome, #e8f2ff highlights).
   Home: it flows slowly behind the star. Product: it warms the black as you reach the bottom. */
(function () {
  const body = document.body;
  const home = body.classList.contains('template-index');
  const product = body.classList.contains('template-product');
  if (!home && !product) return;
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const canvas = document.createElement('canvas');
  canvas.className = 'metal';
  canvas.setAttribute('aria-hidden', 'true');
  body.prepend(canvas);

  const gl = canvas.getContext('webgl', { antialias: false, alpha: true, powerPreference: 'low-power' });
  if (!gl) { canvas.remove(); return; }                    // no WebGL: the page keeps its plain black

  const VERT = `attribute vec2 p; void main(){ gl_Position = vec4(p, 0.0, 1.0); }`;

  const FRAG = `
  precision highp float;
  uniform vec2  uRes;
  uniform float uTime;
  uniform vec2  uMouse;      // 0..1, off-screen when the pointer left
  uniform float uAmount;     // how much metal shows through
  uniform float uHome;       // 1 on the home page: light gathers in the middle, behind the star

  // value noise and fbm, the cheap way
  float hash (vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float noise (vec2 p) {
    vec2 i = floor(p), f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
               mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
  }
  float fbm (vec2 p) {
    float v = 0.0, a = 0.5;
    for (int i = 0; i < 5; ++i) { v += a * noise(p); p *= 2.02; a *= 0.5; }
    return v;
  }

  void main () {
    vec2 uv = gl_FragCoord.xy / uRes;
    vec2 p  = (gl_FragCoord.xy - 0.5 * uRes) / uRes.y;   // square-ish, centred
    float t = uTime * 0.045;

    // two folds of domain warping: this is what makes it look poured rather than painted
    float q = fbm(p * 0.85 + vec2(t, -t * 0.7));
    float r = fbm(p * 1.15 + q * 1.2 + vec2(-t * 0.6, t * 0.4));
    float v = fbm(p * 1.5 + r * 1.5 + vec2(t * 0.3, t * 0.5));

    // chrome: tight bright bands where the surface turns, the way polished metal catches a room
    float bands  = sin((v * 4.2 + r * 1.4) * 3.14159);
    float chrome = pow(0.5 + 0.5 * bands, 4.0);
    float sheen  = pow(0.5 + 0.5 * sin((v * 9.0) * 3.14159), 18.0) * 0.5;

    // the pointer lights the metal, like the cursor wake in the plugin
    float d = distance(uv, uMouse);
    float cursor = exp(-d * 5.0) * 0.5;

    // where the metal is allowed to show
    float shape = mix(smoothstep(0.15, 1.0, uv.y * 0.55 + 0.45 - length(p) * 0.35),
                      smoothstep(0.85, 0.05, length(p) * 1.15),
                      uHome);

    float amount = uAmount * shape;
    vec3 ground = mix(vec3(0.020, 0.020, 0.024), vec3(0.043, 0.043, 0.055), smoothstep(0.0, 1.0, 1.0 - length(p)));
    vec3 steel  = mix(vec3(0.141, 0.145, 0.165), vec3(0.784, 0.796, 0.816), chrome);
    vec3 col    = mix(ground, steel, clamp(amount * (0.55 + chrome * 0.8 + cursor), 0.0, 1.0));
    col += vec3(0.91, 0.95, 1.0) * sheen * amount * (0.35 + cursor);

    float grain = (hash(gl_FragCoord.xy + fract(uTime) * 100.0) - 0.5) * 0.015;
    gl_FragColor = vec4(col + grain, 1.0);
  }`;

  function shader (type, src) {
    const s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) { console.warn(gl.getShaderInfoLog(s)); return null; }
    return s;
  }
  const vs = shader(gl.VERTEX_SHADER, VERT), fs = shader(gl.FRAGMENT_SHADER, FRAG);
  if (!vs || !fs) { canvas.remove(); return; }
  const prog = gl.createProgram();
  gl.attachShader(prog, vs); gl.attachShader(prog, fs); gl.linkProgram(prog); gl.useProgram(prog);

  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  const loc = gl.getAttribLocation(prog, 'p');
  gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

  const U = (n) => gl.getUniformLocation(prog, n);
  const uRes = U('uRes'), uTime = U('uTime'), uMouse = U('uMouse'), uAmount = U('uAmount'), uHome = U('uHome');
  gl.uniform1f(uHome, home ? 1.0 : 0.0);

  let w = 0, h = 0;
  function size () {
    const dpr = Math.min(devicePixelRatio || 1, 1.5);       // a slow background needs no retina
    w = Math.round(innerWidth * dpr); h = Math.round(innerHeight * dpr);
    canvas.width = w; canvas.height = h;
    canvas.style.width = innerWidth + 'px'; canvas.style.height = innerHeight + 'px';
    gl.viewport(0, 0, w, h);
    gl.uniform2f(uRes, w, h);
  }
  size();
  addEventListener('resize', size);

  let mx = -1, my = -1, tmx = -1, tmy = -1;
  addEventListener('mousemove', (e) => { tmx = e.clientX / innerWidth; tmy = 1 - e.clientY / innerHeight; }, { passive: true });
  addEventListener('mouseleave', () => { tmx = tmy = -1; });

  // ?metal=0.8 in the address bar tries another intensity, for judging it live
  const asked = parseFloat(new URLSearchParams(location.search).get('metal'));
  const dial = isFinite(asked) ? Math.max(0, Math.min(1.4, asked)) : 1;

  const start = performance.now();
  let last = 0;
  function frame (now) {
    requestAnimationFrame(frame);
    if (document.hidden) return;
    if (now - last < 33) return;                            // 30 images par seconde suffisent
    last = now;

    mx += (tmx - mx) * 0.06; my += (tmy - my) * 0.06;

    // on the product page the metal rises with the page: black at the top, chrome at the bottom
    let amount = 0.46 * dial;
    if (product) {
      const max = Math.max(1, document.documentElement.scrollHeight - innerHeight);
      amount = (0.1 + 0.85 * Math.min(1, scrollY / max)) * dial;
    }
    gl.uniform1f(uTime, (now - start) / 1000);
    gl.uniform2f(uMouse, mx, my);
    gl.uniform1f(uAmount, amount);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }
  requestAnimationFrame(frame);
})();
